import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { AUDIENCE_TYPES } from "./homepageHero.constants";
import { getPlainTextLength } from "./homepageHero.helpers";
import HeroImageUploader from "./HeroImageUploader";
import DeviceSlidePreview from "./DeviceSlidePreview";
import SlideButtonsEditor from "./SlideButtonsEditor";

const HERO_FONTS = ["arial", "georgia", "times-new-roman", "verdana", "tahoma", "trebuchet", "courier-new", "helvetica"];
const HERO_FONT_LABELS = {
  arial: "Arial",
  georgia: "Georgia",
  "times-new-roman": "Times New Roman",
  verdana: "Verdana",
  tahoma: "Tahoma",
  trebuchet: "Trebuchet MS",
  "courier-new": "Courier New",
  helvetica: "Helvetica",
};
const HERO_FONT_SIZES = Array.from({ length: 63 }, (_, index) => `${index + 10}px`);
const FontFormat = Quill.import("formats/font");
const SizeStyle = Quill.import("attributors/style/size");
FontFormat.whitelist = HERO_FONTS;
SizeStyle.whitelist = HERO_FONT_SIZES;
Quill.register(FontFormat, true);
Quill.register(SizeStyle, true);

function FieldError({ children }) {
  return children ? <div className="hero-field-error">{children}</div> : null;
}

function Section({ icon, title, description, children, className = "" }) {
  return (
    <section className={`hero-form-section ${className}`}>
      <header className="hero-form-section__header">
        <span className="hero-section-icon">{icon}</span>
        <div>
          <h4>{title}</h4>
          {description && <p>{description}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

function HeroCmsTextEditor({ label, value, onChange, onImmediateChange, maxLength, error, disabled, placeholder, allowLists = false }) {
  const count = getPlainTextLength(value);
  const quillRef = useRef(null);
  const lastSelection = useRef(null);
  const toolbarId = `hero-toolbar-${useId().replace(/:/g, "")}`;
  const [fontSize, setFontSize] = useState(16);
  const [fontFamily, setFontFamily] = useState("arial");
  const [textColor, setTextColor] = useState("#18293d");
  // const [backgroundColor, setBackgroundColor] = useState("#ffffff");

  const applyFontSize = (requestedSize) => {
    const nextSize = Math.min(72, Math.max(10, Number(requestedSize) || 16));
    setFontSize(nextSize);
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const selection = lastSelection.current || editor.getSelection(true);
    if (selection?.length) editor.formatText(selection.index, selection.length, "size", `${nextSize}px`, "user");
    else editor.format("size", `${nextSize}px`, "user");
  };

  const applyTextFormat = (format, value) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const selection = lastSelection.current || editor.getSelection(true);
    if (!selection) return;

    editor.setSelection(selection.index, selection.length, "silent");
    if (selection.length) editor.formatText(selection.index, selection.length, format, value || false, "user");
    else editor.format(format, value || false, "user");
    editor.focus();
  };

  const handleSelectionChange = (selection, source, editor) => {
    if (!selection) return;
    lastSelection.current = selection;
    const selectedSize = editor.getFormat(selection).size;
    const selectedFont = editor.getFormat(selection).font;
    if (selectedSize) setFontSize(Number.parseInt(selectedSize, 10) || 16);
    if (selectedFont && HERO_FONTS.includes(selectedFont)) setFontFamily(selectedFont);
  };

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor || !value) return undefined;

    // ReactQuill restores the saved HTML after the toolbar state is created.
    // Read the first formatted character so the native controls also reflect
    // the values returned by the API after saving or reloading the page.
    const frame = window.requestAnimationFrame(() => {
      const contentLength = Math.max(0, editor.getLength() - 1);
      if (!contentLength) return;
      const format = editor.getFormat(0, contentLength);
      if (typeof format.font === "string" && HERO_FONTS.includes(format.font)) {
        setFontFamily(format.font);
      }
      if (typeof format.size === "string") {
        setFontSize(Number.parseInt(format.size, 10) || 16);
      }
      if (typeof format.color === "string") setTextColor(format.color);
      // if (typeof format.background === "string") setBackgroundColor(format.background);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [value]);

  const editorModules = useMemo(() => ({ toolbar: { container: `#${toolbarId}` } }), [toolbarId]);

  return (
    <div
      className={`hero-editor-field ${error ? "has-error" : ""} ${disabled ? "is-disabled" : ""}`}
      onInputCapture={(event) => {
        // ReactQuill can report the HTML from the previous edit in this form.
        // A native input event fires after contenteditable has changed, so use
        // the editor DOM as the source of truth for every user keystroke.
        if (event.target.classList?.contains("ql-editor")) {
          onImmediateChange(event.target.innerHTML);
        }
      }}
    >
      <label>{label} *</label>
      <div className="hero-quill-toolbar-shell">
        <select
          className="hero-font-family-select"
          value={fontFamily}
          disabled={disabled}
          aria-label="Font family"
          style={{ fontFamily: HERO_FONT_LABELS[fontFamily] }}
          onChange={(event) => {
            const selectedFont = event.target.value;
            setFontFamily(selectedFont);
            applyTextFormat("font", selectedFont);
          }}
        >
          {HERO_FONTS.map((font) => <option key={font} value={font} style={{ fontFamily: HERO_FONT_LABELS[font] }}>{HERO_FONT_LABELS[font]}</option>)}
        </select>
        <span className="hero-font-size-control">
          <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyFontSize(fontSize - 1)} aria-label="Decrease font size">−</button>
          <input type="number" min="10" max="72" value={fontSize} disabled={disabled} aria-label="Font size in pixels" onChange={(event) => setFontSize(event.target.value)} onBlur={() => applyFontSize(fontSize)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); applyFontSize(fontSize); } }} />
          <span>px</span>
          <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyFontSize(Number(fontSize) + 1)} aria-label="Increase font size">+</button>
        </span>
        <div id={toolbarId} className="hero-quill-actions">
          {allowLists && <><button className="ql-list" value="ordered" aria-label="Ordered list" /><button className="ql-list" value="bullet" aria-label="Bullet list" /></>}
          <select className="ql-align" aria-label="Text alignment" />
          <button className="ql-bold" aria-label="Bold" />
          <button className="ql-italic" aria-label="Italic" />
          <button className="ql-underline" aria-label="Underline" />
        </div>
        <label className="hero-native-color" title="Text color">
          <span className="hero-native-color__letter">A</span>
          <span className="hero-native-color__line" style={{ backgroundColor: textColor }} />
          <input type="color" value={textColor} disabled={disabled} aria-label="Text color" onChange={(event) => { setTextColor(event.target.value); applyTextFormat("color", event.target.value); }} />
        </label>
        {/* <label className="hero-native-color hero-native-color--background" title="Text background color">
          <span className="hero-native-color__letter">A</span>
          <span className="hero-native-color__fill" style={{ backgroundColor }} />
          <input type="color" value={backgroundColor} disabled={disabled} aria-label="Text background color" onChange={(event) => { setBackgroundColor(event.target.value); applyTextFormat("background", event.target.value); }} />
        </label> */}
      </div>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        readOnly={disabled}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={(previousSelection, source, editor) => onChange(editor.getHTML())}
        onChangeSelection={handleSelectionChange}
        modules={editorModules}
        formats={["font", "size", "list", "align", "bold", "italic", "underline", "color", "background"]}
      />
      <div className="hero-editor-meta">
        <FieldError>{error}</FieldError>
        <span className={count > maxLength ? "is-over-limit" : ""}>{count} / {maxLength}</span>
      </div>
    </div>
  );
}

export default function HeroSlideForm({ slide, errors = {}, update, updateImmediate, disabled, dispatch, isNew, isFallback = false }) {
  const [activeTab, setActiveTab] = useState("content");
  const [contentLanguage, setContentLanguage] = useState("en");

  useEffect(() => {
    if (errors.title || errors.title_nl || errors.subtitle || errors.subtitle_nl || errors.audienceType) {
      setActiveTab("content");
      if (errors.title_nl || errors.subtitle_nl) setContentLanguage("nl");
      else if (errors.title || errors.subtitle) setContentLanguage("en");
    }
    else if (errors.buttons) setActiveTab("actions");
    else if (errors.desktopImage || errors.tabletImage || errors.mobileImage || errors.startDateTime || errors.endDateTime) setActiveTab("media");
  }, [errors]);

  return (
    <div className="hero-editor-layout">
      <nav className="hero-editor-tabs" aria-label="Slide editor sections">
        <button type="button" className={activeTab === "content" ? "is-active" : ""} onClick={() => setActiveTab("content")}><span>T</span>Content & audience</button>
        <button type="button" className={activeTab === "actions" ? "is-active" : ""} onClick={() => setActiveTab("actions")}><span>↗</span>Buttons</button>
        <button type="button" className={activeTab === "media" ? "is-active" : ""} onClick={() => setActiveTab("media")}><span>▧</span>Images & schedule</button>
      </nav>

      <div className="hero-editor-tab-grid">
        <main className="hero-editor-tab-panel">
          {activeTab === "content" && (
            <>
              <Section icon="T" title="Content" description="Main headline and supporting copy shown on the hero banner.">
                <div className="hero-editor-language-switch" role="tablist" aria-label="Content language">
                  <button type="button" role="tab" aria-selected={contentLanguage === "en"} className={contentLanguage === "en" ? "is-active" : ""} onClick={() => setContentLanguage("en")}>English</button>
                  <button type="button" role="tab" aria-selected={contentLanguage === "nl"} className={contentLanguage === "nl" ? "is-active" : ""} onClick={() => setContentLanguage("nl")}>Dutch</button>
                </div>
                <div hidden={contentLanguage !== "en"}>
                    <HeroCmsTextEditor key="title" label="Title (English)" value={slide.title} maxLength={150} error={errors.title} disabled={disabled} placeholder="Enter English hero headline" onChange={(value) => update("title", value)} onImmediateChange={(value) => updateImmediate("title", value)} />
                    <HeroCmsTextEditor key="subtitle" label="Subtitle (English)" value={slide.subtitle} maxLength={300} error={errors.subtitle} disabled={disabled} placeholder="Enter English supporting copy" allowLists onChange={(value) => update("subtitle", value)} onImmediateChange={(value) => updateImmediate("subtitle", value)} />
                </div>
                <div hidden={contentLanguage !== "nl"}>
                    <HeroCmsTextEditor key="title_nl" label="Title (Dutch)" value={slide.title_nl} maxLength={150} error={errors.title_nl} disabled={disabled} placeholder="Enter Dutch hero headline" onChange={(value) => update("title_nl", value)} onImmediateChange={(value) => updateImmediate("title_nl", value)} />
                    <HeroCmsTextEditor key="subtitle_nl" label="Subtitle (Dutch)" value={slide.subtitle_nl} maxLength={300} error={errors.subtitle_nl} disabled={disabled} placeholder="Enter Dutch supporting copy" allowLists onChange={(value) => update("subtitle_nl", value)} onImmediateChange={(value) => updateImmediate("subtitle_nl", value)} />
                </div>
              </Section>

              <Section icon="◎" title="Audience and layout" description="Choose who sees this slide and how its content is aligned.">
                <div className="hero-audience-field">
                  <div className="hero-field">
                    <label>Audience *</label>
                    <select value={slide.audienceType} disabled={disabled} onChange={(event) => update("audienceType", event.target.value)}>
                      {AUDIENCE_TYPES.map((audience) => <option key={audience.value} value={audience.value}>{audience.label}</option>)}
                    </select>
                    <FieldError>{errors.audienceType}</FieldError>
                  </div>
                </div>
                <div className="hero-device-layouts">
                  <div className="hero-device-layout-group">
                    <div className="hero-device-layout-group__heading"><span className="hero-device-layout-icon">▰</span><div><strong>Desktop view</strong><small>Content position on desktop screens</small></div></div>
                    <div className="hero-device-layout-fields">
                      <div className="hero-field">
                    <label>Horizontal position</label>
                    <select value={slide.desktopLayout || slide.contentPosition || "start"} disabled={disabled} onChange={(event) => update("desktopLayout", event.target.value)}>
                      <option value="start">Left</option><option value="center">Center</option><option value="end">Right</option>
                    </select>
                  </div>
                  <div className="hero-field">
                    <label>Vertical position</label>
                    <select value={slide.desktopVerticalLayout || "center"} disabled={disabled} onChange={(event) => update("desktopVerticalLayout", event.target.value)}>
                      <option value="start">Top</option><option value="center">Center</option><option value="end">Bottom</option>
                    </select>
                  </div>
                    </div>
                  </div>
                  <div className="hero-device-layout-group">
                    <div className="hero-device-layout-group__heading"><span className="hero-device-layout-icon">▭</span><div><strong>Tablet view</strong><small>Content position on tablet screens</small></div></div>
                    <div className="hero-device-layout-fields">
                      <div className="hero-field">
                    <label>Horizontal position</label>
                    <select value={slide.tabletLayout || slide.contentPosition || "start"} disabled={disabled} onChange={(event) => update("tabletLayout", event.target.value)}>
                      <option value="start">Left</option><option value="center">Center</option><option value="end">Right</option>
                    </select>
                  </div>
                  <div className="hero-field">
                    <label>Vertical position</label>
                    <select value={slide.tabletVerticalLayout || "center"} disabled={disabled} onChange={(event) => update("tabletVerticalLayout", event.target.value)}>
                      <option value="start">Top</option><option value="center">Center</option><option value="end">Bottom</option>
                    </select>
                  </div>
                    </div>
                  </div>
                  <div className="hero-device-layout-group">
                    <div className="hero-device-layout-group__heading"><span className="hero-device-layout-icon">▯</span><div><strong>Mobile view</strong><small>Content position on mobile screens</small></div></div>
                    <div className="hero-device-layout-fields">
                      <div className="hero-field">
                    <label>Horizontal position</label>
                    <select value={slide.mobileLayout || slide.contentPosition || "start"} disabled={disabled} onChange={(event) => update("mobileLayout", event.target.value)}>
                      <option value="start">Left</option><option value="center">Center</option><option value="end">Right</option>
                    </select>
                  </div>
                  <div className="hero-field">
                    <label>Vertical position</label>
                    <select value={slide.mobileVerticalLayout || "center"} disabled={disabled} onChange={(event) => update("mobileVerticalLayout", event.target.value)}>
                      <option value="start">Top</option><option value="center">Center</option><option value="end">Bottom</option>
                    </select>
                  </div>
                    </div>
                  </div>
                </div>
              </Section>
            </>
          )}

          {activeTab === "actions" && (
            <SlideButtonsEditor buttons={slide.buttons || []} errors={errors.buttons || []} disabled={disabled} onChange={(buttons) => update("buttons", buttons)} />
          )}

          {activeTab === "media" && (
            <>
              <Section icon="▧" title="Responsive images" description="Upload separate artwork optimized for desktop, tablet and mobile screens.">
                <div className="hero-images-grid hero-images-grid--three">
                  <HeroImageUploader device="desktop" label="Desktop image" value={slide.desktopImage} onChange={(value) => update("desktopImage", value)} aspect={1920 / 720} dimensions="1920 × 720" outputWidth={1920} outputHeight={720} disabled={disabled} fieldError={errors.desktopImage} dispatch={dispatch} />
                  <HeroImageUploader device="tablet" label="Tablet image" value={slide.tabletImage} onChange={(value) => update("tabletImage", value)} aspect={1024 / 760} dimensions="1024 × 760" outputWidth={1024} outputHeight={760} disabled={disabled} fieldError={errors.tabletImage} dispatch={dispatch} />
                  <HeroImageUploader device="mobile" label="Mobile image" value={slide.mobileImage} onChange={(value) => update("mobileImage", value)} aspect={760 / 960} dimensions="760 × 960" outputWidth={760} outputHeight={960} disabled={disabled} fieldError={errors.mobileImage} dispatch={dispatch} />
                </div>
              </Section>

              {!isFallback && <Section icon="◷" title="Schedule" description="Control when this slide can appear on the homepage.">
                <label className="hero-toggle-row hero-permanent-toggle">
                  <span><strong>Permanent slide</strong><small>Show this slide continuously without a start or end date.</small></span>
                  <input
                    type="checkbox"
                    checked={Boolean(slide.is_permanent)}
                    disabled={disabled}
                    onChange={(event) => {
                      const permanent = event.target.checked;
                      update("is_permanent", permanent);
                      if (permanent) {
                        update("startDateTime", null);
                        update("endDateTime", null);
                      }
                    }}
                  />
                </label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <div className="hero-fields-grid hero-date-grid">
                    <div><DateTimePicker label="Start date and time" value={slide.startDateTime ? dayjs(slide.startDateTime) : null} disabled={disabled || slide.is_permanent} disablePast minDateTime={isNew ? dayjs() : undefined} onChange={(value) => update("startDateTime", value?.isValid() ? value.toISOString() : null)} /><FieldError>{errors.startDateTime}</FieldError></div>
                    <div><DateTimePicker label="End date and time" value={slide.endDateTime ? dayjs(slide.endDateTime) : null} disabled={disabled || slide.is_permanent} disablePast minDateTime={slide.startDateTime ? dayjs(slide.startDateTime) : dayjs()} onChange={(value) => update("endDateTime", value?.isValid() ? value.toISOString() : null)} /><FieldError>{errors.endDateTime}</FieldError></div>
                  </div>
                </LocalizationProvider>
              </Section>}
            </>
          )}
        </main>

        <DeviceSlidePreview slide={slide} />
      </div>
    </div>
  );
}
